import { useCallback } from 'react';

const MUTATION_OBSERVER_OPTIONS: MutationObserverInit = {
	childList: true,
	subtree: true,
	attributes: true,
	attributeFilter: ['aria-hidden'],
};

/**
 * Stripe injects a decoy `<input aria-hidden="true">` into every Element mount to block autofill,
 * but leaves it focusable — failing "aria-hidden elements must not be focusable" (unpatched
 * upstream bug: stripe/react-stripe-js#236). We fix it by making the decoy `inert`.
 *
 * `inert` rather than `tabindex="-1"` is load-bearing. The real inputs live in a cross-origin
 * iframe, so stripe.js handles tabbing out of the last subfield itself: on keypress it posts a
 * `redirectfocus` message, then the parent finds the anchor by locating the Stripe mount element
 * among the page's focusable elements and focuses the next (or previous) one. `tabindex="-1"`
 * removes the decoy — the only focusable element inside the mount — from that list, so the anchor
 * lookup fails and focus jumps to the *first* focusable element on the page instead of the next
 * one. `inert` keeps the decoy in Stripe's list (its filter only checks tabindex, size, and
 * visibility) while still making the browser and axe-core treat it as non-focusable, and fails
 * safe: where `inert` is unsupported we're back to the original a11y violation, not broken tab
 * order.
 *
 * Callback ref rather than `useEffect`: the sweep and observer attach/detach exactly on the
 * container's mount/unmount, not on every render.
 */
export function useHideAriaHiddenStripeInputsFromTabOrder(): (node: HTMLElement | null) => (() => void) | undefined {
	return useCallback((node: HTMLElement | null) => {
		if (!node) return undefined;

		const hideFromTabOrder = () => {
			node.querySelectorAll<HTMLInputElement>('input[aria-hidden="true"]').forEach((input) => {
				input.setAttribute('inert', '');
			});
		};

		hideFromTabOrder();

		const observer = new MutationObserver(hideFromTabOrder);
		observer.observe(node, MUTATION_OBSERVER_OPTIONS);

		return () => observer.disconnect();
	}, []);
}
